import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { categoriesApi } from '../api/categories.api';
import type { Category, CreateCategoryDto, UpdateCategoryDto } from '../types';

export const useCategories = (sectionId?: number) => {
  return useQuery<Category[]>({
    queryKey: ['categories', 'list', sectionId],
    queryFn: () => categoriesApi.getAll(sectionId),
  });
};

export const useCategoriesGroupedBySection = () => {
  return useQuery<Record<number, Category[]>>({
    queryKey: ['categories', 'grouped-by-section'],
    queryFn: () => categoriesApi.getAllGroupedBySection(),
  });
};

export const usePopularCategories = () => {
  return useQuery<Category[]>({
    queryKey: ['categories', 'collections', 'popular'],
    queryFn: () => categoriesApi.getPopular(),
  });
};

export const useCategoriesWithoutSections = () => {
  return useQuery<Category[]>({
    queryKey: ['categories', 'without-sections'],
    queryFn: () => categoriesApi.getWithoutSections(),
  });
};

export const useCategory = (id: number) => {
  return useQuery<Category>({
    queryKey: ['categories', 'detail', id],
    queryFn: () => categoriesApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, { data: CreateCategoryDto; file?: File }>({
    mutationFn: ({ data, file }) => categoriesApi.create(data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, { id: number; data: UpdateCategoryDto; file?: File }>({
    mutationFn: ({ id, data, file }) => categoriesApi.update(id, data, file),
    onSuccess: (_, variables) => {
      // Инвалидируем все списки категорий (с любым sectionId и без)
      queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
      // Инвалидируем конкретную категорию
      queryClient.invalidateQueries({ queryKey: ['categories', 'detail', variables.id] });
      // Инвалидируем группировку по секциям
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
    },
  });
};

export const useHideCategory = () => {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, { id: number; isHidden: boolean }>({
    mutationFn: ({ id, isHidden }) => categoriesApi.setHidden(id, isHidden),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'detail', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
    },
  });
};

export const useSetCategoryPopular = () => {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, { id: number; isPopular: boolean }>({
    mutationFn: ({ id, isPopular }) => categoriesApi.setPopular(id, isPopular),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'detail', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'collections', 'popular'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => categoriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
      queryClient.invalidateQueries({ queryKey: ['home-tiles'] });
      notifications.show({
        title: 'Успешно',
        message: 'Категория успешно удалена',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при удалении категории',
        color: 'red',
      });
    },
  });
};

export const useAddCategoryToSection = () => {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, { id: number; sectionId: number }>({
    mutationFn: ({ id, sectionId }) => categoriesApi.addToSection(id, sectionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'detail', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
    },
  });
};

export const useRemoveCategoryFromSection = () => {
  const queryClient = useQueryClient();

  return useMutation<Category, Error, { id: number; sectionId: number }>({
    mutationFn: ({ id, sectionId }) => categoriesApi.removeFromSection(id, sectionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'detail', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
      notifications.show({
        title: 'Успешно',
        message: 'Категория успешно удалена из секции',
        color: 'green',
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Ошибка при удалении категории из секции';
      notifications.show({
        title: 'Ошибка',
        message: errorMessage,
        color: 'red',
      });
    },
  });
};

